import { CommonModule, formatDate } from '@angular/common';
import { Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AuthService } from '@app/core/api/auth/auth.service';
import {
    PaginationMeta,
    ColumnDef,
    RowAction,
    DataTableComponent,
} from '@app/shared/components/data-table/data-table.component';
import { ModalService } from '@app/shared/components/modal/modal.service';
import { ToastService } from '@app/shared/components/toast/toast.service';
import { ButtonComponent } from '@app/shared/forms/button/button.component';
import { InputComponent } from '@app/shared/forms/input/input.component';
import { FileUploadComponent, FileUploadItem } from '@app/shared/forms/file-upload/file-upload.component';
import {
    CircleAlert,
    Eye,
    File,
    Flag,
    Loader,
    LucideAngularModule,
    Pin,
    PinOff,
    RefreshCcw,
    Search,
    UserRoundMinus,
    UserRoundPlus,
    UserRoundX,
} from 'lucide-angular';
import { Subject, debounceTime, distinctUntilChanged, take, takeUntil } from 'rxjs';
import { InvestListResponse, InvestService, InvestUserRequest } from '@app/core/api/users/invest.service';
import { OnlineUsersService } from '@app/core/services/online-users.service';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ComboBoxComponent, ComboBoxOption } from '@app/shared/forms/combo-box/combo-box.component';
import { getFieldError, isFieldInvalid } from '@app/helpers/validators/custom-form-validation';
import { reportProfileValidationMap } from '@app/helpers/validation-maps/report-profile.map';
import { DatePickerComponent } from '@app/shared/forms/date-picker/date-picker.component';
import { toApiDate } from '@app/helpers/utils/date.utils';

@Component({
    selector: 'app-investor',
    standalone: true,
    imports: [
        DataTableComponent,
        ComboBoxComponent,
        DatePickerComponent,
        InputComponent,
        ButtonComponent,
        FileUploadComponent,
        LucideAngularModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
    ],
    templateUrl: './investor.component.html',
    styleUrl: './investor.component.scss',
})
export class InvestorComponent implements OnInit, OnDestroy {
    authService = inject(AuthService);
    investService = inject(InvestService);
    modalService = inject(ModalService);
    toastService = inject(ToastService);
    private fb = inject(FormBuilder);
    private router = inject(Router);
    private onlineUsersService = inject(OnlineUsersService);

    @ViewChild('connectionModal') connectionModal!: TemplateRef<any>;
    @ViewChild('pendingRequestModal') pendingRequestModal!: TemplateRef<any>;
    @ViewChild('connectionDisconnectModal') connectionDisconnectModal!: TemplateRef<any>;
    @ViewChild('reportModal') reportModalTemplate!: TemplateRef<any>;
    @ViewChild('modalTemplate') modalTemplate!: TemplateRef<any>;

    reportForm: FormGroup;
    attachmentFiles: FileUploadItem[] = [];
    violations: ComboBoxOption[] = [];
    triggersOthers: string[] = [];
    violationsLoaded = false;

    readonly Search = Search;
    readonly RefreshCcw = RefreshCcw;
    readonly File = File;
    readonly Eye = Eye;
    readonly CircleAlert = CircleAlert;
    readonly Loader = Loader;

    destroy$ = new Subject<void>();
    searchSubject$ = new Subject<string>();

    search = '';
    lastUpdated = '';
    recordCount = 0;
    type: 'Investor' | 'Investee' = 'Investor';

    page = 1;
    total = 0;
    pinned = 0;
    online = 0;
    connections = 0;

    isSubmitted = false;
    loading = false;
    readonly user = this.authService.user();
    currentUser: any;
    currentRow: any;
    onlineUsers = new Set<number>();

    allUsers: any[] = [];
    meta: PaginationMeta = {
        from: 0,
        to: 0,
        total: 0,
        current_page: 1,
        last_page: 1,
    };

    investColumn: ColumnDef[] = [];

    actionsDefault: RowAction[] = [
        {
            id: 1,
            icon: (row: any) => (row.usersPinned ? PinOff : Pin),
            tooltip: (row: any) => `${row?.usersPinned ? 'Unpin' : 'Pin'} ${this.type} profile`,
            callback: (row) => this.pin(row),
            show: true,
            buttonClass: 'text-primary hover:bg-blue-50',
        },
        {
            id: 2,
            icon: Eye,
            tooltip: (row: any) => `View ${this.type} details`,
            callback: (row) => this.link(row),
            show: true,
            buttonClass: 'text-primary hover:bg-blue-50',
        },
        {
            id: 3,
            icon: (row: any) => row?.statusIcon,
            tooltip: (row: any) =>
                `${
                    row?.connectionStatus == 'pending'
                        ? 'Cancel request'
                        : row?.connectionStatus == 'accepted'
                          ? 'Disconnect'
                          : 'Connect'
                } with ${this.type}`,
            callback: (row) => this.link(row),
            show: true,
            buttonClass: 'text-primary hover:bg-blue-50',
        },
        {
            id: 4,
            icon: Flag,
            tooltip: (row: any) => `Report ${this.type} profile`,
            callback: (row) => this.report(row),
            show: true,
            disabled: (row) => row.has_active_delisting,
            buttonClass: (row) => {
                return row.has_active_delisting ? 'text-gray-300 hover:bg-blue-50' : 'text-primary hover:bg-blue-50';
            },
        },
    ];

    constructor() {
        this.reportForm = this.fb.group({
            violations_others: ['', [Validators.maxLength(150)]],
            violations: [[], [Validators.required]],
            files: [null],
        });

        this.reportForm.get('violations')?.valueChanges.subscribe(() => {
            const ctrl = this.reportForm.get('violations_others');
            if (this.isGeneralViolationSelected) {
                ctrl?.setValidators([Validators.required, Validators.maxLength(150)]);
            } else {
                ctrl?.setValue('');
                ctrl?.clearValidators();
            }
            ctrl?.updateValueAndValidity();
        });
    }

    ngOnInit() {
        this.type = this.user?.type == 'Investor' ? 'Investee' : 'Investor';
        this.setInvestColumn();
        this.getCurrentUser();
        this.loadViolations();

        this.onlineUsersService.onlineUsers$.pipe(takeUntil(this.destroy$)).subscribe((users) => {
            this.type = this.user?.type == 'Investor' ? 'Investee' : 'Investor';
            this.onlineUsers = new Set(users.filter((u) => u.type == this.type && u.status == 9).map((u) => u.id));
        });

        // Set up debounced search
        this.searchSubject$
            .pipe(
                debounceTime(500), // Wait 500ms after the user stops typing
                distinctUntilChanged(), // Only emit if the search term is different
                takeUntil(this.destroy$),
            )
            .subscribe((searchTerm: string) => {
                this.search = searchTerm;
                this.page = 1; // Reset to first page
                this.loadUsers();
            });
    }

    get columns(): ColumnDef[] {
        return this.investColumn;
    }
    get actions(): RowAction[] {
        return this.actionsDefault;
    }

    get isGeneralViolationSelected(): boolean {
        if (!this.triggersOthers.length) return false;
        const violations = this.reportForm.get('violations')?.value || [];
        return violations.some((v: string) => this.triggersOthers.includes(v));
    }

    getCurrentUser() {
        this.loading = true;
        this.authService
            .getCurrentUser(true)
            .pipe(take(1))
            .subscribe({
                next: (user) => {
                    this.currentUser = user.data;
                    this.type = this.currentUser?.type == 'Investor' ? 'Investee' : 'Investor';
                    this.pinned = parseInt(this.currentUser?.pinned);
                    this.connections = parseInt(this.currentUser?.connections);

                    this.setInvestColumn();
                    this.loadUsers();
                },
                error: (err) => {
                    console.error('Error fetching current user:', err);
                    this.loading = false;
                },
            });
    }

    loadUsers() {
        this.loading = true;
        let params = new HttpParams().set('page', this.page.toString());
        params = params.set('type', this.type ?? this.currentUser.type);

        if (this.search) params = params.set('search', this.search);
        if (this.lastUpdated) params = params.set('narrative_updated_at', toApiDate(this.lastUpdated));
        const recordCount = Number(this.recordCount);
        if (recordCount) params = params.set('count', recordCount.toString());

        this.investService
            .getAllUsers(params)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: InvestListResponse) => {
                    const users = this.sanitizedRes(res.data);

                    const sortedUsers = this.sortPinned(users);
                    this.allUsers = this.sortRow(sortedUsers);

                    this.meta = this.getMeta(res);
                    this.total = this.meta.total;
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                },
            });
    }

    sanitizedRes(data: any) {
        return data.map(
            (
                row: {
                    connections: any[];
                    investee_code: any;
                    pinned_entities: any[];
                    pinned_by_entities: any[];
                    id: string | number;
                },
                i: any,
            ) => {
                const matchedConnection = row.connections?.find((connect: any) =>
                    this.type === 'Investor'
                        ? connect.investee_id == this.currentUser.invest_id
                        : connect.investor_id == this.currentUser.invest_id,
                );

                const connectionStatus = matchedConnection?.status ?? null;
                const chatId = matchedConnection?.chat_id ?? null;

                const nickname = matchedConnection?.nickname ?? null;
                const displayCode = nickname ? `${row.investee_code} - ${nickname}` : row.investee_code;

                return {
                    ...row,
                    code: displayCode,
                    nickname,
                    chatId,

                    usersPinned:
                        row.pinned_by_entities?.some(
                            (pinned: any) => pinned.pinned_by_entity_id === this.currentUser.invest_id,
                        ) ?? false,

                    connectionStatus,

                    statusIcon:
                        connectionStatus === 'pending'
                            ? UserRoundX
                            : connectionStatus === 'accepted'
                              ? UserRoundMinus
                              : UserRoundPlus,

                    isOnline: this.isUserOnline(row.id) ? '🟢Online' : '🔴Offline',
                };
            },
        );
    }

    sortPinned(data: any) {
        return data.sort(
            (a: { usersPinned: any }, b: { usersPinned: any }) => (b.usersPinned ? 1 : 0) - (a.usersPinned ? 1 : 0),
        );
    }

    sortRow(data: any) {
        return data.map((row: any, i: number) => ({ ...row, rowId: i + 1 }));
    }

    getMeta(res: any): PaginationMeta {
        return {
            from: res.meta?.from ?? 0,
            to: res.meta?.to ?? 0,
            total: res.meta?.total ?? 0,
            current_page: res.meta?.current_page ?? 1,
            last_page: res.meta?.last_page ?? 1,
        };
    }

    setInvestColumn() {
        this.investColumn = [
            { key: 'rowId', label: '#' },
            this.type == 'Investor'
                ? { key: 'registration_name', label: 'Corporate Name' }
                : { key: 'code', label: 'Investee Code' },
            {
                key: 'profile_last_updated',
                label: 'Last Updated',
                valueFn: (row) =>
                    row.profile_last_updated
                        ? formatDate(row.profile_last_updated, 'MM/dd/yyyy', 'en-US', 'UTC')
                        : row.profile_created_at
                          ? formatDate(row.profile_created_at, 'MM/dd/yyyy', 'en-US', 'UTC')
                          : '--',
            },
            {
                key: 'isOnline',
                label: 'Status',
                valueFn: (row: any) => {
                    return this.isUserOnline(row.id) ? '🟢Online' : '🔴Offline';
                },
            },
        ];
    }

    onSearchChange(event: any) {
        const searchTerm = event.target.value;
        this.searchSubject$.next(searchTerm);
    }

    onPageChange(newPage: number) {
        this.page = newPage;
        this.loadUsers();
    }

    view(row: any) {
        this.investService.setState({
            id: row.invest_id,
            code: row.type == 'Investor' ? row.registration_name : row.investee_code,
            type: this.type,
        });

        this.router.navigate(['profile-narrative']);
    }

    link(row: any) {
        console.log('navigating to connect-with-users');
        this.investService.setState({
            id: row.invest_id,
            // code: row.type == 'Investor' ? row.registration_name : row.investee_code,
            // type: row.type,
            // investorId: row.type == 'Investor' ? row.invest_id : this.currentUser?.invest_id,
            // investeeId: row.type == 'Investee' ? row.invest_id : this.currentUser?.invest_id,
            code: this.type == 'Investor' ? row.registration_name : row.code,
            type: this.type,
            investorId: this.type == 'Investee' ? this.currentUser?.invest_id : row.invest_id,
            investeeId: this.type == 'Investor' ? this.currentUser?.invest_id : row.invest_id,
            status: row.connectionStatus,
            nickname: row.nickname,
            chatId: row.chatId,
        });

        this.router.navigate(['connect-with-users']);
    }

    pin(row: any) {
        const investRequest: Partial<InvestUserRequest> = {
            pinned_entity_id: row.invest_id,
            invest_entity_id: this.currentUser.invest_id,
        };

        if (!row.usersPinned) {
            const modal = this.modalService.open({
                title: `Pin ${this.type} Profile`,
                contentTemplate: this.modalTemplate,
                contentData: {
                    currentRow: row,
                    onConfirm: () => {
                        this.isSubmitted = true;
                        this.loading = true;
                        this.investService
                            .pin(investRequest)
                            .pipe(takeUntil(this.destroy$))
                            .subscribe({
                                next: () => {
                                    this.toastService.show({
                                        title: 'Success',
                                        content: `${this.type} pinned successfully`,
                                        type: 'success',
                                        duration: 5000,
                                    });

                                    this.loading = false;
                                    this.isSubmitted = false;
                                    this.getCurrentUser();
                                    this.modalService.close(modal);
                                },
                                error: () => {
                                    this.toastService.show({
                                        title: 'Error',
                                        content: 'Pinned account limit reached',
                                        type: 'error',
                                        duration: 5000,
                                    });
                                    this.loading = false;
                                    this.isSubmitted = false;
                                    this.modalService.close(modal);
                                },
                            });
                    },
                    onCancel: () => {
                        this.modalService.close(modal);
                        this.loading = false;
                    },
                },
                showCloseButton: false,
                backdropDismiss: false,
                keyboardClose: false,
            });
        } else {
            const modal = this.modalService.open({
                title: `Unpin ${this.type} Profile`,
                contentTemplate: this.modalTemplate,
                contentData: {
                    currentRow: row,
                    onConfirm: () => {
                        this.isSubmitted = true;
                        this.loading = true;
                        this.investService
                            .unpin(investRequest)
                            .pipe(takeUntil(this.destroy$))
                            .subscribe({
                                next: () => {
                                    this.toastService.show({
                                        title: 'Success',
                                        content: `${this.type} unpinned successfully`,
                                        type: 'success',
                                        duration: 5000,
                                    });
                                    this.loading = false;
                                    this.isSubmitted = false;
                                    this.getCurrentUser();
                                    this.modalService.close(modal);
                                },
                                error: () => {
                                    this.toastService.show({
                                        title: 'Error',
                                        content: 'Pinned entity not found',
                                        type: 'error',
                                        duration: 5000,
                                    });
                                    this.loading = false;
                                    this.isSubmitted = false;
                                    this.modalService.close(modal);
                                },
                            });
                    },
                    onCancel: () => {
                        this.modalService.close(modal);
                        this.loading = false;
                    },
                },
                showCloseButton: false,
                backdropDismiss: false,
                keyboardClose: false,
            });
        }
    }

    report(row: any) {
        console.log(row);
        this.currentRow = row;
        this.reportForm.reset();
        this.attachmentFiles = [];
        const modal = this.modalService.open({
            title: `Report ${this.type}`,
            contentTemplate: this.reportModalTemplate,
            contentData: {
                currentRow: row,
                onSubmit: () => {
                    if (this.reportForm.invalid) {
                        this.reportForm.markAllAsTouched();
                        return;
                    }

                    const formData = new FormData();
                    formData.append('invest_entity_id', (this.currentRow?.invest_id ?? null)?.toString() || '');
                    formData.append('initiated_by_user_id', (this.currentUser?.id ?? null)?.toString() || '');

                    // Add violations
                    const violations = this.reportForm.value.violations;
                    if (violations && violations.length > 0) {
                        violations.forEach((violation: string, index: number) => {
                            formData.append(`violations[${index}]`, violation);
                        });
                    }

                    // Add violations_others
                    if (this.reportForm.value.violations_others) {
                        formData.append('violations_others', this.reportForm.value.violations_others);
                    }

                    this.reportForm.disable();

                    // Validate and add files (required: min 1, max 5)
                    if (this.attachmentFiles.length === 0) {
                        this.toastService.show({
                            title: 'Validation Error',
                            content: 'Please attach at least one supporting document.',
                            type: 'error',
                            duration: 5000,
                        });
                        this.loading = false;
                        this.reportForm.enable();
                        return;
                    }

                    this.attachmentFiles.forEach((item, index) => {
                        formData.append(`files[${index}]`, item.file);
                    });

                    const confirm = this.modalService.open({
                        title: `Report ${this.type} profile`,
                        contentTemplate: this.modalTemplate,
                        contentData: {
                            currentRow: row,
                            onConfirm: () => {
                                this.loading = true;
                                this.isSubmitted = true;
                                this.submit(modal, formData, confirm);
                            },
                            onCancel: () => {
                                this.modalService.close(confirm);
                                this.loading = false;
                                this.isSubmitted = false;
                            },
                        },
                        showCloseButton: false,
                        backdropDismiss: false,
                        keyboardClose: false,
                    });
                },
                onCancel: () => {
                    this.modalService.close(modal);
                },
            },
            keyboardClose: false,
            backdropDismiss: false,
        });
    }

    clearFilters() {
        this.page = 1;
        this.search = '';
        this.lastUpdated = '';
        this.recordCount = 0;
        this.loadUsers();
    }

    onDateChange() {
        this.page = 1;
        this.loadUsers();
    }

    onRecordChange() {
        this.page = 1;
        this.loadUsers();
    }

    onRowAction({ action, row }: { action: RowAction; row: any }) {
        if (action.callback) action.callback(row);
    }

    isUserOnline(userId: number | string): boolean {
        return this.onlineUsers.has(Number(userId));
    }

    onFileSelected(files: FileUploadItem[]) {
        this.attachmentFiles = files;
    }

    onFileRemoved(fileId: string) {
        this.attachmentFiles = this.attachmentFiles.filter((f) => f.id !== fileId);
    }

    onFileValidationError(event: any) {
        this.toastService.show({
            title: 'File Validation Error',
            content: event || 'Invalid file selected',
            type: 'error',
            duration: 5000,
        });
    }

    submit(modal: number, formData: FormData, confirm: any) {
        this.isSubmitted = true;

        this.investService
            .report(formData)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.toastService.show({
                        title: 'Success',
                        content: response?.message || `${this.type} reported successfully`,
                        type: 'success',
                        duration: 3000,
                    });
                    this.attachmentFiles = [];
                    this.reportForm.reset();
                    this.modalService.close(confirm);
                    this.modalService.close(modal);
                    window.location.reload();
                },
                error: (error) => {
                    console.error('Error creating configuration:', error);
                    this.toastService.show({
                        title: 'Error',
                        content: error?.error?.message || `Failed to report ${this.type}`,
                        type: 'error',
                        duration: 5000,
                    });
                    this.loading = false;
                    this.reportForm.enable();
                    this.modalService.close(confirm);
                    this.modalService.close(modal);
                },
                complete: () => {
                    if (this.reportForm.disabled) {
                        this.reportForm.enable();
                    }
                    this.loading = false;
                    this.isSubmitted = false;
                },
            });
    }

    isFieldInvalid(fieldName: string): boolean {
        return isFieldInvalid(this.reportForm, fieldName, this.isSubmitted);
    }

    getFieldError(fieldName: string): string | null {
        return getFieldError(this.reportForm, fieldName, reportProfileValidationMap, this.isSubmitted);
    }

    isImageFile(filename: string): boolean {
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
        const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
        return imageExtensions.includes(extension);
    }

    formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    downloadFile(url: string, filename: string): void {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    loadViolations() {
        this.investService
            .getPublicConfigurations()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (configs: any[]) => {
                    const config = configs.find((c: any) => c.key === 'violation_guidelines');
                    if (config) {
                        const parsed = JSON.parse(config.value);
                        const list = parsed.items ?? parsed;
                        const triggers = parsed.triggers_others ?? [];
                        this.triggersOthers = Array.isArray(triggers) ? triggers : [triggers];
                        this.violations = list.map((v: string) => ({
                            label: v,
                            value: v,
                        }));
                    }
                    this.violationsLoaded = true;
                },
                error: (err) => {
                    console.error('Error loading violations:', err);
                    this.violationsLoaded = true;
                },
            });
    }

    // getFormattedDateTime(dateString: string | null, pattern: string): string {
    //     if (!dateString) return 'N/A';
    //     return formatDate(dateString, pattern);
    // }

    /**
     * Cleans up subscriptions to prevent memory leaks.
     * @returns void
     */
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
